import { CommonModule } from '@angular/common';
import {
  Component,
  OnDestroy,
  OnInit,
  inject,
  signal,
} from '@angular/core';
import { ToastService } from 'angular-toastify';
import { Subscription } from 'rxjs';

type PssToastKind = 'info' | 'success' | 'warning' | 'error' | 'default';

interface PssToastItem {
  id: number;
  message: string;
  kind: PssToastKind;
  leaving: boolean;
  durationMs: number;
}

const AUTO_CLOSE_MS = 2800;
const EXIT_MS = 280;

@Component({
  selector: 'app-pss-toast-container',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './pss-toast-container.component.html',
  styleUrl: './pss-toast-container.component.css',
})
export class PssToastContainerComponent implements OnInit, OnDestroy {
  readonly toasts = signal<PssToastItem[]>([]);

  private readonly toastService = inject(ToastService);
  private readonly timers = new Map<number, ReturnType<typeof setTimeout>>();
  private sub = new Subscription();
  private seq = 0;

  ngOnInit(): void {
    this.sub.add(
      this.toastService.toastAddedEvent.subscribe((toast: { message?: string; type?: number }) => {
        this.push(toast?.message ?? '', this.mapKind(toast?.type));
      })
    );
    this.sub.add(
      this.toastService.dismissAllEvent.subscribe(() => this.dismissAll())
    );
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
    this.clearAllTimers();
  }

  trackById(_: number, item: PssToastItem): number {
    return item.id;
  }

  dismiss(id: number): void {
    const current = this.toasts();
    const target = current.find((t) => t.id === id);
    if (!target || target.leaving) return;

    this.clearTimers(id);
    this.toasts.update((list) =>
      list.map((t) => (t.id === id ? { ...t, leaving: true } : t))
    );

    const exitTimer = setTimeout(() => {
      this.toasts.update((list) => list.filter((t) => t.id !== id));
      this.timers.delete(id);
    }, EXIT_MS);
    this.timers.set(id, exitTimer);
  }

  iconFor(kind: PssToastKind): string {
    switch (kind) {
      case 'success':
        return 'check_circle';
      case 'error':
        return 'error';
      case 'warning':
        return 'warning';
      case 'info':
        return 'info';
      default:
        return 'notifications';
    }
  }

  labelFor(kind: PssToastKind): string {
    switch (kind) {
      case 'success':
        return 'สำเร็จ';
      case 'error':
        return 'ผิดพลาด';
      case 'warning':
        return 'แจ้งเตือน';
      case 'info':
        return 'ข้อมูล';
      default:
        return 'ข้อความ';
    }
  }

  private push(message: string, kind: PssToastKind): void {
    const text = (message || '').trim();
    if (!text) return;

    const id = ++this.seq;
    const item: PssToastItem = {
      id,
      message: text,
      kind,
      leaving: false,
      durationMs: AUTO_CLOSE_MS,
    };

    this.toasts.update((list) => [item, ...list].slice(0, 5));
    this.timers.set(
      id,
      setTimeout(() => this.dismiss(id), AUTO_CLOSE_MS)
    );
  }

  private dismissAll(): void {
    for (const t of [...this.toasts()]) this.dismiss(t.id);
  }

  private clearTimers(id: number): void {
    const t = this.timers.get(id);
    if (t) clearTimeout(t);
    this.timers.delete(id);
  }

  private clearAllTimers(): void {
    for (const id of [...this.timers.keys()]) this.clearTimers(id);
  }

  private mapKind(type: number | undefined): PssToastKind {
    switch (type) {
      case 0:
        return 'info';
      case 1:
        return 'success';
      case 2:
        return 'warning';
      case 3:
        return 'error';
      default:
        return 'default';
    }
  }
}
